/**
 * Chiffre à composer devant un numéro (décision William 31/10/2026) — la règle, en un seul endroit côté écran :
 *   - ligne imposée par l'écran (`ligne`) si elle est valable ;
 *   - une personne INTERNE (collègue) → 0 ;
 *   - une fiche rattachée à une société sous contrat ou interne → 0 (liste lue sur /api/call/prefixes, même règle que
 *     le serveur : numero_client_svc) ;
 *   - tout le reste (particulier, société sans contrat, inconnu) → 2.
 * Le 1 (MCF / formation) n'est donné par aucune règle automatique pour l'instant.
 * Rien n'est affiché à un compte qui n'est pas interne (client, prestataire, aperçu « comme un client »).
 */
import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from './api';
import { isUserInternalForOrg } from './roles';
import { LIGNES_APPEL, PREFIXES_APPEL, phoneToDialable } from './phone';
// Espaces de noms : une page testée avec une doublure PARTIELLE de ces modules (sans useOptionalAuth / useOrg)
// ne doit pas tomber à cause du badge — sans eux, simplement aucun chiffre.
import * as authHooks from '../hooks/useAuth';
import * as orgSelector from '../components/OrgSelector';

let promesse = null;
let dernier = null;
let chargeLe = 0;
// Un contrat signé ou résilié dans la journée doit changer le chiffre sans recharger la page (relecture du 31/10).
const DUREE_CACHE_MS = 10 * 60 * 1000;

/** Lecture unique (partagée par tous les écrans) ; `null` si refusée ou en panne — on retentera au prochain montage. */
export function chargerPrefixesAppel() {
  if (promesse && Date.now() - chargeLe > DUREE_CACHE_MS) promesse = null;
  if (!promesse) {
    chargeLe = Date.now();
    promesse = apiFetch('/api/call/prefixes')
      .then((r) => (r && r.ok ? r.json() : null))
      .then((d) => {
        if (!d || d.ok !== true || !Array.isArray(d.societes_ligne_meteris)) throw new Error('prefixes illisibles');
        dernier = { ligneMeteris: new Set(d.societes_ligne_meteris) };
        return dernier;
      })
      .catch(() => {
        promesse = null;
        return null;
      });
  }
  return promesse;
}

/** Pour les tests : oublie la lecture en cache. */
export function oublierPrefixesAppel() {
  promesse = null;
  dernier = null;
  chargeLe = 0;
}

/** Règle pure : chiffre à composer, ou '' si on ne sait pas (rien chargé). */
export function prefixePour(donnees, { clientId, interne, ligne } = {}) {
  if (ligne && Object.prototype.hasOwnProperty.call(LIGNES_APPEL, ligne)) return String(ligne);
  if (!donnees) return '';
  if (interne) return PREFIXES_APPEL.meteris;
  if (clientId && donnees.ligneMeteris.has(clientId)) return PREFIXES_APPEL.meteris;
  return PREFIXES_APPEL.atelier;
}

// Numéro du standard qui a reçu l'appel → chiffre de la même ligne (rappel d'un inconnu).
const LIGNE_PAR_NUMERO_APPELE = { '0183623327': '0', '0185840320': '1', '0184744495': '2' };

export function ligneDuNumeroAppele(numero) {
  return LIGNE_PAR_NUMERO_APPELE[phoneToDialable(numero)] || '';
}

/**
 * Contexte d'un appel ou d'un message vocal : société connue → sa règle ; contact connu sans société (particulier) → 2 ;
 * appelant inconnu → la ligne qu'il a appelée (01 83 → 0, 01 85 → 1, 01 84 → 2) quand l'appel la porte (`did_number` ;
 * les messages vocaux ne la portent pas : règle par défaut, 2).
 */
export function contexteAppel(appel) {
  const a = appel || {};
  const clientId = a.client_id || a.company_id;
  if (clientId) return { clientId };
  if (a.user_id) return {};
  const ligne = ligneDuNumeroAppele(a.did_number);
  return ligne ? { ligne } : {};
}

/** Hook : rend `(contexte) => '0' | '1' | '2' | ''`. Toujours '' pour un compte non interne. */
export function usePrefixeAppel() {
  const auth = typeof authHooks.useOptionalAuth === 'function' ? authHooks.useOptionalAuth() : null;
  const org = typeof orgSelector.useOrg === 'function' ? orgSelector.useOrg() : null;
  const compte = auth ? (auth.effectiveUser || auth.user) : null;
  const estInterne = isUserInternalForOrg(compte, org?.activeOrg?.code);
  const [donnees, setDonnees] = useState(dernier);
  useEffect(() => {
    if (!estInterne) return undefined;
    let vivant = true;
    chargerPrefixesAppel().then((d) => { if (vivant) setDonnees(d); });
    return () => { vivant = false; };
  }, [estInterne]);
  return useCallback(
    (contexte = {}) => (estInterne ? prefixePour(donnees, contexte) : ''),
    [estInterne, donnees],
  );
}
